import React, { useState, useEffect } from 'react';
import {
  Search, Plus, Building2, FileText, DollarSign, CheckCircle, Clock,
  XCircle, ChevronRight, AlertTriangle, CreditCard, Eye
} from 'lucide-react';
import {
  getVendors, getVendorSummary, getVendor, createVendor, updateVendor,
  getInvoices, createInvoice, approveInvoice, payInvoice, voidInvoice
} from '../../services/adminDealFlowApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/new-components/ui/card';
import { Button } from '../../components/new-components/ui/button';
import { Badge } from '../../components/new-components/ui/badge';
import { Input } from '../../components/new-components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/new-components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/new-components/ui/select';

const formatCurrency = (cents: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);

const unwrap = (res: any) => {
  const body = res?.data?.success !== undefined ? res.data : (res?.success !== undefined ? res : res?.data || res);
  return body;
};

const statusColor = (s: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (s) {
    case 'active': case 'paid': return 'default';
    case 'approved': case 'scheduled': return 'secondary';
    case 'inactive': case 'suspended': case 'cancelled': case 'void': case 'failed': return 'destructive';
    default: return 'outline';
  }
};

type View = 'vendors' | 'invoices' | 'vendor-detail';

const Vendors: React.FC = () => {
  const [view, setView] = useState<View>('vendors');
  const [vendors, setVendors] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [selected, setSelected] = useState<any>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  // Dialogs
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [showAddInvoice, setShowAddInvoice] = useState(false);

  // Form state
  const [vendorForm, setVendorForm] = useState({
    business_name: '', contact_name: '', email: '', phone: '',
    tax_id: '', tax_type: 'ein' as 'ein' | 'ssn',
    address_line1: '', city: '', state: '', zip: '',
    bank_name: '', ach_routing: '', ach_account_number: '', ach_account_type: 'checking' as 'checking' | 'savings',
    payment_terms: 'net_30', notes: '',
  });
  const [invoiceForm, setInvoiceForm] = useState({
    vendor_id: '', invoice_number: '', description: '',
    amount: '', gl_code: '', invoice_date: '', due_date: '',
  });

  // ── Data loading ──────────────────────────────────────────
  const loadVendors = async () => {
    setLoading(true);
    try {
      const res = unwrap(await getVendors({ search: search || undefined, status: statusFilter || undefined, page }));
      setVendors(res.data || []);
      setPagination(res.pagination);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadSummary = async () => {
    try {
      const res = unwrap(await getVendorSummary());
      setSummary(res.data);
    } catch (e) { console.error(e); }
  };

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const params: any = { page, search: search || undefined, status: statusFilter || undefined };
      if (selected?.id) params.vendor_id = selected.id;
      const res = unwrap(await getInvoices(params));
      setInvoices(res.data || []);
      setPagination(res.pagination);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadVendorDetail = async (id: string) => {
    try {
      const res = unwrap(await getVendor(id));
      setSelected(res.data);
      setView('vendor-detail');
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (view === 'vendors') { loadVendors(); loadSummary(); }
    if (view === 'invoices') loadInvoices();
  }, [view, page, statusFilter]);

  useEffect(() => {
    const t = setTimeout(() => { if (view === 'vendors') loadVendors(); else if (view === 'invoices') loadInvoices(); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // ── Actions ───────────────────────────────────────────────
  const handleCreateVendor = async () => {
    try {
      await createVendor({
        ...vendorForm,
        tax_id: vendorForm.tax_id || undefined,
      });
      setShowAddVendor(false);
      setVendorForm({
        business_name: '', contact_name: '', email: '', phone: '',
        tax_id: '', tax_type: 'ein', address_line1: '', city: '', state: '', zip: '',
        bank_name: '', ach_routing: '', ach_account_number: '', ach_account_type: 'checking' as 'checking' | 'savings',
        payment_terms: 'net_30', notes: '',
      });
      loadVendors();
      loadSummary();
    } catch (e) { console.error(e); }
  };

  const handleCreateInvoice = async () => {
    if (!invoiceForm.vendor_id || !invoiceForm.amount) return;
    try {
      await createInvoice({
        vendor_id: invoiceForm.vendor_id,
        invoice_number: invoiceForm.invoice_number || undefined,
        description: invoiceForm.description || undefined,
        amount_cents: Math.round(Number(invoiceForm.amount) * 100),
        gl_code: invoiceForm.gl_code || undefined,
        invoice_date: invoiceForm.invoice_date || undefined,
        due_date: invoiceForm.due_date || undefined,
      });
      setShowAddInvoice(false);
      setInvoiceForm({ vendor_id: '', invoice_number: '', description: '', amount: '', gl_code: '', invoice_date: '', due_date: '' });
      loadInvoices();
      loadSummary();
    } catch (e) { console.error(e); }
  };

  const handleApprove = async (id: string) => {
    try { await approveInvoice(id); loadInvoices(); loadSummary(); } catch (e) { console.error(e); }
  };
  const handlePay = async (id: string) => {
    if (!window.confirm('Initiate ACH payment for this invoice?')) return;
    try { await payInvoice(id); loadInvoices(); loadSummary(); } catch (e) { console.error(e); }
  };
  const handleVoid = async (id: string) => {
    if (!window.confirm('Void this invoice?')) return;
    try { await voidInvoice(id); loadInvoices(); loadSummary(); } catch (e) { console.error(e); }
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="space-y-5 sm:space-y-6 min-w-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold">Vendor Management</h1>
          <p className="text-sm text-muted-foreground">Manage vendors, invoices, and bill payments</p>
        </div>
        <div className="grid grid-cols-2 sm:flex gap-2 w-full sm:w-auto">
          <Button variant={view === 'vendors' ? 'default' : 'outline'} size="sm" onClick={() => { setView('vendors'); setPage(1); setStatusFilter(''); }}>
            <Building2 className="h-4 w-4 mr-1" /> Vendors
          </Button>
          <Button variant={view === 'invoices' ? 'default' : 'outline'} size="sm" onClick={() => { setView('invoices'); setPage(1); setStatusFilter(''); }}>
            <FileText className="h-4 w-4 mr-1" /> Invoices
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="text-sm text-muted-foreground">Active Vendors</div>
              <div className="text-2xl font-bold">{Number(summary.active_vendors || 0)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="text-sm text-muted-foreground">Pending Invoices</div>
              <div className="text-2xl font-bold">{Number(summary.pending_invoices || 0)}</div>
              <div className="text-xs text-muted-foreground">{formatCurrency(Number(summary.pending_amount_cents || 0))}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="text-sm text-muted-foreground">Total Paid</div>
              <div className="text-2xl font-bold">{formatCurrency(Number(summary.total_paid_cents || 0))}</div>
              <div className="text-xs text-muted-foreground">{Number(summary.paid_invoices || 0)} invoices</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="text-sm text-muted-foreground flex items-center gap-1">
                <AlertTriangle className="h-3 w-3 text-amber-500" /> Overdue
              </div>
              <div className="text-2xl font-bold text-amber-600">{Number(summary.overdue_invoices || 0)}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Toolbar */}
      <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto] gap-2 sm:items-center">
        <div className="relative min-w-0 sm:max-w-sm">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder={view === 'vendors' ? 'Search vendors...' : 'Search invoices...'} value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-full sm:w-[140px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {view === 'vendors'
              ? <>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </>
              : <>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </>
            }
          </SelectContent>
        </Select>
        {view === 'vendors' && (
          <Button onClick={() => setShowAddVendor(true)}><Plus className="h-4 w-4 mr-1" /> Add Vendor</Button>
        )}
        {view === 'invoices' && (
          <Button onClick={() => { setInvoiceForm({ ...invoiceForm, vendor_id: selected?.id || '' }); setShowAddInvoice(true); }}>
            <Plus className="h-4 w-4 mr-1" /> New Invoice
          </Button>
        )}
      </div>

      {/* ── VENDOR LIST ────────────────────────────────────── */}
      {view === 'vendors' && (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm min-w-[500px]">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="text-left p-3 font-medium">Vendor</th>
                  <th className="text-left p-3 font-medium hidden md:table-cell">Contact</th>
                  <th className="text-left p-3 font-medium hidden md:table-cell">Terms</th>
                  <th className="text-right p-3 font-medium">Invoices</th>
                  <th className="text-right p-3 font-medium">Total Paid</th>
                  <th className="text-left p-3 font-medium">Status</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {vendors.map(v => (
                  <tr key={v.id} className="border-b hover:bg-muted/20 cursor-pointer" onClick={() => loadVendorDetail(v.id)}>
                    <td className="p-3">
                      <div className="font-medium">{v.business_name}</div>
                      <div className="text-xs text-muted-foreground">{v.vendor_id}</div>
                    </td>
                    <td className="p-3 hidden md:table-cell">
                      <div>{v.contact_name || '—'}</div>
                      <div className="text-xs text-muted-foreground">{v.email || '—'}</div>
                    </td>
                    <td className="p-3 hidden md:table-cell capitalize">{(v.payment_terms || 'net_30').replace('_', ' ')}</td>
                    <td className="p-3 text-right">{Number(v.invoice_count || 0)}</td>
                    <td className="p-3 text-right font-mono">{formatCurrency(Number(v.total_paid_cents || 0))}</td>
                    <td className="p-3">
                      <Badge variant={statusColor(v.status)}>{v.status}</Badge>
                    </td>
                    <td className="p-3"><ChevronRight className="h-4 w-4 text-muted-foreground" /></td>
                  </tr>
                ))}
                {!vendors.length && !loading && (
                  <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">No vendors found</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* ── INVOICE LIST ───────────────────────────────────── */}
      {view === 'invoices' && (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm min-w-[500px]">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="text-left p-3 font-medium">Invoice</th>
                  <th className="text-left p-3 font-medium">Vendor</th>
                  <th className="text-left p-3 font-medium hidden md:table-cell">Description</th>
                  <th className="text-right p-3 font-medium">Amount</th>
                  <th className="text-left p-3 font-medium hidden md:table-cell">Due Date</th>
                  <th className="text-left p-3 font-medium">Status</th>
                  <th className="p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => (
                  <tr key={inv.id} className="border-b hover:bg-muted/20">
                    <td className="p-3">
                      <div className="font-medium">{inv.invoice_number || inv.invoice_id}</div>
                      <div className="text-xs text-muted-foreground">{inv.invoice_id}</div>
                    </td>
                    <td className="p-3">{inv.vendor_name || '—'}</td>
                    <td className="p-3 hidden md:table-cell max-w-[200px] truncate">{inv.description || '—'}</td>
                    <td className="p-3 text-right font-mono font-medium">{formatCurrency(Number(inv.amount_cents))}</td>
                    <td className="p-3 hidden md:table-cell">
                      {inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '—'}
                      {inv.due_date && new Date(inv.due_date) < new Date() && ['draft','approved','scheduled'].includes(inv.status) && (
                        <span className="ml-1 text-amber-500 text-xs">(overdue)</span>
                      )}
                    </td>
                    <td className="p-3"><Badge variant={statusColor(inv.status)}>{inv.status}</Badge></td>
                    <td className="p-3">
                      <div className="flex gap-1">
                        {inv.status === 'draft' && (
                          <Button size="sm" variant="outline" onClick={() => handleApprove(inv.id)}>
                            <CheckCircle className="h-3.5 w-3.5 mr-1" /> Approve
                          </Button>
                        )}
                        {['approved', 'scheduled'].includes(inv.status) && (
                          <Button size="sm" onClick={() => handlePay(inv.id)}>
                            <CreditCard className="h-3.5 w-3.5 mr-1" /> Pay
                          </Button>
                        )}
                        {['draft', 'approved', 'scheduled'].includes(inv.status) && (
                          <Button size="sm" variant="ghost" onClick={() => handleVoid(inv.id)}>
                            <XCircle className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!invoices.length && !loading && (
                  <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">No invoices found</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* ── VENDOR DETAIL ──────────────────────────────────── */}
      {view === 'vendor-detail' && selected && (
        <div className="space-y-4">
          <Button variant="outline" size="sm" onClick={() => { setView('vendors'); setSelected(null); }}>
            ← Back to Vendors
          </Button>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" /> {selected.business_name}
                  <Badge variant={statusColor(selected.status)} className="ml-2">{selected.status}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-muted-foreground">ID:</span> {selected.vendor_id}</div>
                <div><span className="text-muted-foreground">Contact:</span> {selected.contact_name || '—'}</div>
                <div><span className="text-muted-foreground">Email:</span> {selected.email || '—'}</div>
                <div><span className="text-muted-foreground">Phone:</span> {selected.phone || '—'}</div>
                <div><span className="text-muted-foreground">Terms:</span> {(selected.payment_terms || 'net_30').replace('_', ' ')}</div>
                <div><span className="text-muted-foreground">Tax ID:</span> {selected.tax_id_last4 ? `***-**-${selected.tax_id_last4}` : '—'}</div>
                <div><span className="text-muted-foreground">Bank:</span> {selected.bank_name || '—'}</div>
                <div><span className="text-muted-foreground">Account:</span> {selected.ach_account_last4 ? `****${selected.ach_account_last4}` : '—'}</div>
                {selected.address_line1 && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Address:</span>{' '}
                    {selected.address_line1}{selected.city ? `, ${selected.city}` : ''}{selected.state ? `, ${selected.state}` : ''} {selected.zip}
                  </div>
                )}
                {selected.notes && <div className="col-span-2"><span className="text-muted-foreground">Notes:</span> {selected.notes}</div>}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-sm">Quick Actions</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <Button className="w-full" size="sm" onClick={() => { setInvoiceForm({ ...invoiceForm, vendor_id: selected.id }); setShowAddInvoice(true); }}>
                  <Plus className="h-4 w-4 mr-1" /> New Invoice
                </Button>
                <Button variant="outline" className="w-full" size="sm" onClick={() => { setView('invoices'); setStatusFilter(''); }}>
                  <Eye className="h-4 w-4 mr-1" /> View All Invoices
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Vendor's Invoices */}
          <Card>
            <CardHeader><CardTitle className="text-sm">Invoices</CardTitle></CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm min-w-[400px]">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left p-3 font-medium">Invoice #</th>
                    <th className="text-left p-3 font-medium">Description</th>
                    <th className="text-right p-3 font-medium">Amount</th>
                    <th className="text-left p-3 font-medium">Due</th>
                    <th className="text-left p-3 font-medium">Status</th>
                    <th className="p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(selected.invoices || []).map((inv: any) => (
                    <tr key={inv.id} className="border-b">
                      <td className="p-3 font-medium">{inv.invoice_number || inv.invoice_id}</td>
                      <td className="p-3 max-w-[200px] truncate">{inv.description || '—'}</td>
                      <td className="p-3 text-right font-mono">{formatCurrency(Number(inv.amount_cents))}</td>
                      <td className="p-3">{inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '—'}</td>
                      <td className="p-3"><Badge variant={statusColor(inv.status)}>{inv.status}</Badge></td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          {inv.status === 'draft' && <Button size="sm" variant="outline" onClick={() => handleApprove(inv.id)}>Approve</Button>}
                          {['approved','scheduled'].includes(inv.status) && <Button size="sm" onClick={() => handlePay(inv.id)}>Pay</Button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!(selected.invoices || []).length && (
                    <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No invoices yet</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.pages > 1 && view !== 'vendor-detail' && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <span className="text-sm py-2 px-3">Page {page} of {pagination.pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}

      {/* ── ADD VENDOR DIALOG ──────────────────────────────── */}
      <Dialog open={showAddVendor} onOpenChange={setShowAddVendor}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add Vendor</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-sm font-medium">Business Name *</label>
              <Input value={vendorForm.business_name} onChange={e => setVendorForm({ ...vendorForm, business_name: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Contact Name</label>
              <Input value={vendorForm.contact_name} onChange={e => setVendorForm({ ...vendorForm, contact_name: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Email</label>
              <Input type="email" value={vendorForm.email} onChange={e => setVendorForm({ ...vendorForm, email: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Phone</label>
              <Input value={vendorForm.phone} onChange={e => setVendorForm({ ...vendorForm, phone: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Payment Terms</label>
              <Select value={vendorForm.payment_terms} onValueChange={v => setVendorForm({ ...vendorForm, payment_terms: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="due_on_receipt">Due on Receipt</SelectItem>
                  <SelectItem value="net_15">Net 15</SelectItem>
                  <SelectItem value="net_30">Net 30</SelectItem>
                  <SelectItem value="net_45">Net 45</SelectItem>
                  <SelectItem value="net_60">Net 60</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2 border-t pt-3 mt-1">
              <h4 className="font-medium text-sm mb-2">Tax Information</h4>
            </div>
            <div>
              <label className="text-sm font-medium">Tax ID (EIN/SSN)</label>
              <Input value={vendorForm.tax_id} onChange={e => setVendorForm({ ...vendorForm, tax_id: e.target.value })} placeholder="XX-XXXXXXX" />
            </div>
            <div>
              <label className="text-sm font-medium">Tax Type</label>
              <Select value={vendorForm.tax_type} onValueChange={v => setVendorForm({ ...vendorForm, tax_type: v as 'ein' | 'ssn' })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ein">EIN (Business)</SelectItem>
                  <SelectItem value="ssn">SSN (Individual)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2 border-t pt-3 mt-1">
              <h4 className="font-medium text-sm mb-2">Address</h4>
            </div>
            <div className="col-span-2">
              <Input placeholder="Street address" value={vendorForm.address_line1} onChange={e => setVendorForm({ ...vendorForm, address_line1: e.target.value })} />
            </div>
            <div>
              <Input placeholder="City" value={vendorForm.city} onChange={e => setVendorForm({ ...vendorForm, city: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="State" value={vendorForm.state} onChange={e => setVendorForm({ ...vendorForm, state: e.target.value })} />
              <Input placeholder="ZIP" value={vendorForm.zip} onChange={e => setVendorForm({ ...vendorForm, zip: e.target.value })} />
            </div>

            <div className="col-span-2 border-t pt-3 mt-1">
              <h4 className="font-medium text-sm mb-2">Bank Account (for ACH payments)</h4>
            </div>
            <div>
              <label className="text-sm font-medium">Bank Name</label>
              <Input value={vendorForm.bank_name} onChange={e => setVendorForm({ ...vendorForm, bank_name: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Account Type</label>
              <Select value={vendorForm.ach_account_type} onValueChange={v => setVendorForm({ ...vendorForm, ach_account_type: v as 'checking' | 'savings' })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="checking">Checking</SelectItem>
                  <SelectItem value="savings">Savings</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Routing Number</label>
              <Input value={vendorForm.ach_routing} onChange={e => setVendorForm({ ...vendorForm, ach_routing: e.target.value })} placeholder="9 digits" />
            </div>
            <div>
              <label className="text-sm font-medium">Account Number</label>
              <Input value={vendorForm.ach_account_number} onChange={e => setVendorForm({ ...vendorForm, ach_account_number: e.target.value })} />
            </div>

            <div className="col-span-2">
              <label className="text-sm font-medium">Notes</label>
              <textarea className="w-full rounded-md border p-2 text-sm" rows={2} value={vendorForm.notes}
                onChange={e => setVendorForm({ ...vendorForm, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddVendor(false)}>Cancel</Button>
            <Button onClick={handleCreateVendor} disabled={!vendorForm.business_name}>Create Vendor</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── ADD INVOICE DIALOG ─────────────────────────────── */}
      <Dialog open={showAddInvoice} onOpenChange={setShowAddInvoice}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New Invoice</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {!invoiceForm.vendor_id && (
              <div>
                <label className="text-sm font-medium">Vendor *</label>
                <Select value={invoiceForm.vendor_id} onValueChange={v => setInvoiceForm({ ...invoiceForm, vendor_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select vendor" /></SelectTrigger>
                  <SelectContent>
                    {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.business_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="text-sm font-medium">Invoice Number</label>
              <Input value={invoiceForm.invoice_number} onChange={e => setInvoiceForm({ ...invoiceForm, invoice_number: e.target.value })} placeholder="e.g. INV-001" />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Input value={invoiceForm.description} onChange={e => setInvoiceForm({ ...invoiceForm, description: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Amount ($) *</label>
              <Input type="number" step="0.01" min="0" value={invoiceForm.amount} onChange={e => setInvoiceForm({ ...invoiceForm, amount: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">GL Code</label>
              <Input value={invoiceForm.gl_code} onChange={e => setInvoiceForm({ ...invoiceForm, gl_code: e.target.value })} placeholder="Optional" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-sm font-medium">Invoice Date</label>
                <Input type="date" value={invoiceForm.invoice_date} onChange={e => setInvoiceForm({ ...invoiceForm, invoice_date: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium">Due Date</label>
                <Input type="date" value={invoiceForm.due_date} onChange={e => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddInvoice(false)}>Cancel</Button>
            <Button onClick={handleCreateInvoice} disabled={!invoiceForm.vendor_id || !invoiceForm.amount}>Create Invoice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Vendors;
